import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import type { MultiSelections, RangeSelections, ShopifySearchProduct, StorefrontScope } from './storefront-api'
import type { SearchOverviewChat } from './storefront-chat-api'

// Staff-only experiment (USA-1115): POST /dashboard/catalog-search/ai-overview streams
// `data:` JSON events — status, source, delta, then done or error.

export type AiOverviewPlacement = 'autocomplete' | 'results'

export type AiOverviewRequest = {
  query: string
  placement: AiOverviewPlacement
  filters?: MultiSelections
  ranges?: RangeSelections
  scope?: StorefrontScope
  /** A model-number search (USA-1255's check); an exact match gets the one-product layout. */
  modelSearch?: boolean
}

export type AiOverviewEvent =
  | { type: 'status'; text: string }
  | { type: 'source'; ref: number; product: ShopifySearchProduct }
  | { type: 'exact'; ref: number }
  | { type: 'delta'; text: string }
  | { type: 'done'; cited: number[]; chat?: SearchOverviewChat | null }
  | { type: 'error'; message: string }

export type AiOverviewState = {
  phase: 'loading' | 'streaming' | 'done' | 'error'
  status: string
  text: string
  sources: Record<number, ShopifySearchProduct>
  /** The product whose model is the search (USA-1250). */
  exact: number | null
  /** The signed opening "Ask a follow-up" starts the store chat with (USA-1257). */
  chat: SearchOverviewChat | null
}

const LOADING: AiOverviewState = {
  phase: 'loading',
  status: '',
  text: '',
  sources: {},
  exact: null,
  chat: null,
}

export function overviewBody(request: AiOverviewRequest) {
  return JSON.stringify({
    query: request.query.trim(),
    placement: request.placement,
    filters: request.filters ?? {},
    ranges: request.ranges ?? {},
    ...(request.scope ? { scope: { key: request.scope.key, value: request.scope.value } } : {}),
    ...(request.modelSearch ? { model_search: true } : {}),
  })
}

export function applyOverviewEvent(state: AiOverviewState, event: AiOverviewEvent): AiOverviewState {
  switch (event.type) {
    case 'status':
      return { ...state, status: event.text }
    case 'source':
      return { ...state, sources: { ...state.sources, [event.ref]: event.product } }
    case 'exact':
      return { ...state, exact: event.ref }
    case 'delta':
      return { ...state, phase: 'streaming', text: state.text + event.text }
    case 'done':
      return { ...state, phase: 'done', chat: event.chat ?? null }
    case 'error':
      return { ...state, phase: 'error' }
  }
}

// A product the overview names, `[Hoshizaki KM-115BAJ](3)` (USA-1246); a bare `[3]` the model
// slips in still counts as a mention but shows nothing.
const MENTION = /\[([^[\]\n]+)\]\((\d{1,2})\)|\s*\[(\d{1,2})\]/g

export type OverviewPart = string | { name: string | null; ref: number }

/** A line of the answer as text and product mentions. */
export function overviewParts(text: string): OverviewPart[] {
  const parts: OverviewPart[] = []
  let end = 0
  for (const match of text.matchAll(MENTION)) {
    if (match.index > end) parts.push(text.slice(end, match.index))
    parts.push({ name: match[1] ?? null, ref: Number(match[2] ?? match[3]) })
    end = match.index + match[0].length
  }
  if (end < text.length) parts.push(text.slice(end))
  return parts
}

/** Mentioned refs in first-mention order that the overview actually saw. */
export function citedRefs(text: string, sources: AiOverviewState['sources']) {
  const refs = [...text.matchAll(MENTION)].map((match) => Number(match[2] ?? match[3]))
  return [...new Set(refs)].filter((ref) => sources[ref])
}

/** The products a finished overview names, in first-mention order. */
export function citedProducts(state: AiOverviewState | null) {
  if (state?.phase !== 'done') return []
  return citedRefs(state.text, state.sources).flatMap((ref) => state.sources[ref] ?? [])
}

/** The answer as paragraphs and `- ` bullet lists; a half-streamed `[name](3` shows as its name. */
export function overviewBlocks(text: string, streaming: boolean) {
  const visible = streaming
    ? text.replace(/\[([^[\]\n]*)(?:\](?:\(\d{0,2})?)?$/, (_, name: string) => (/^\d*$/.test(name) ? '' : name))
    : text
  const blocks: Array<{ kind: 'p'; text: string } | { kind: 'ul'; items: string[] }> = []
  for (const raw of visible.split('\n')) {
    const line = raw.trim()
    if (!line) continue
    const bullet = /^[-•*]\s+(.*)$/.exec(line)
    const last = blocks.at(-1)
    if (bullet) {
      if (last?.kind === 'ul') last.items.push(bullet[1] ?? '')
      else blocks.push({ kind: 'ul', items: [bullet[1] ?? ''] })
    } else blocks.push({ kind: 'p', text: line })
  }
  return blocks
}

export async function streamAiOverview(
  token: string,
  body: string,
  onEvent: (event: AiOverviewEvent) => void,
  signal: AbortSignal,
) {
  const response = await fetch(`${API_BASE_URL}/dashboard/catalog-search/ai-overview`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body,
    signal,
  })
  if (!response.ok || !response.body) throw new Error(`AI overview failed with HTTP ${response.status}`)
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    while (true) {
      const { value, done } = await reader.read()
      buffer += decoder.decode(value, { stream: !done })
      let end: number
      while ((end = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, end)
        buffer = buffer.slice(end + 2)
        const data = block
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trimStart())
          .join('\n')
        if (data) onEvent(JSON.parse(data) as AiOverviewEvent)
      }
      if (done) return
    }
  } finally {
    await reader.cancel().catch(() => undefined)
  }
}

/** Streams the overview for `request` (null = none); finished answers are reused from the query cache. */
export function useAiOverview(request: AiOverviewRequest | null): AiOverviewState | null {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  const body = request && request.query.trim().length >= 2 ? overviewBody(request) : null
  const [live, setLive] = useState<{ body: string; state: AiOverviewState } | null>(null)

  useEffect(() => {
    if (!body || queryClient.getQueryData(['ai-overview', body])) return
    const controller = new AbortController()
    let state = LOADING
    const update = (next: AiOverviewState) => {
      state = next
      setLive({ body, state })
    }
    void (async () => {
      try {
        const token = await getAccessToken()
        if (!token) throw new Error('Not signed in')
        await streamAiOverview(token, body, (event) => update(applyOverviewEvent(state, event)), controller.signal)
        const finished = state.phase === 'error' ? state : { ...state, phase: 'done' as const }
        if (finished.phase === 'done') queryClient.setQueryData(['ai-overview', body], finished)
        update(finished)
      } catch {
        if (!controller.signal.aborted) update({ ...state, phase: 'error' })
      }
    })()
    return () => controller.abort()
  }, [body, getAccessToken, queryClient])

  if (!body) return null
  const cached = queryClient.getQueryData<AiOverviewState>(['ai-overview', body])
  if (cached) return cached
  return live?.body === body ? live.state : LOADING
}
